import * as React from 'react';

import { fetchTranslation as utilFetchTranslation } from './utils/fetchTranslation';
import { replacePlaceholders } from './utils/replacePlaceholder';
import tNodeFactory from './utils/tNodeFactory';

import type { I18nConfig, CollectionId, StringPlaceholders } from './types';

import { Language } from '@shopee-user/global';

enum CollectionState {
  Initial = 0,
  Fetching = 1,
  Ready = 2,
}

type CollectionStateByLanguage = Record<
  Language,
  Record<CollectionId, CollectionState>
>;

export default function useI18n({
  env,
  initialLanguage,
  transifyCollectionMapping = {}, // Non-collection mapping are legacy and we don't need to support that
  initialTranslations = {},
  initialCollections = [],
}: I18nConfig) {
  const [translations, setTranslations] = React.useState<
    Record<Language, Record<string, string>>
  >({ [initialLanguage]: initialTranslations } as Record<
    Language,
    Record<string, string>
  >);

  /**
   * Record of loaded collections by language, we use this
   * to figure out if we should load a new collection
   */
  const collectionsByLanguage = React.useRef<CollectionStateByLanguage>(
    initialCollections.reduce<CollectionStateByLanguage>(
      (acc, colId) => {
        acc[initialLanguage][colId] = CollectionState.Ready;
        return acc;
      },
      {
        [initialLanguage]: {},
      } as CollectionStateByLanguage
    )
  );

  const [currentLanguage, setCurrentLanguage] = React.useState<Language>(
    initialLanguage
  );

  function checkMissingCollection(collectionIds: CollectionId[]) {
    return collectionIds.filter(
      id => !collectionsByLanguage.current[currentLanguage][id]
    );
  }

  async function fetchTranslation(
    collectionIds: CollectionId[],
    language: Language
  ) {
    if (collectionIds.length === 0) return;
    const fetchResponses = await Promise.all(
      collectionIds.map(id => {
        collectionsByLanguage.current[language][id] = CollectionState.Fetching;
        return utilFetchTranslation({
          transifyMapping: transifyCollectionMapping,
          env,
          language,
          collectionId: id,
        }).then(fetchedTranslation => ({ id, fetchedTranslation }));
      })
    );

    let newTranslations: Record<string, string> = {};
    const newCollections: CollectionId[] = [];
    fetchResponses.forEach(({ id, fetchedTranslation }) => {
      newTranslations = { ...newTranslations, ...fetchedTranslation };
      newCollections.push(id);
      collectionsByLanguage.current[language][id] = CollectionState.Ready;
    });

    setTranslations(translations => {
      return {
        ...translations,
        [language]: { ...translations[language], ...newTranslations },
      };
    });
  }

  async function fetchMissingTranslation(collectionIds: CollectionId[]) {
    const collectionToFetch = checkMissingCollection(collectionIds);
    if (collectionToFetch.length > 0) {
      return fetchTranslation(collectionToFetch, currentLanguage);
    }
  }

  const [shouldShowTransifyKey, setShouldShowTransifyKey] = React.useState(
    false
  );
  function updateShouldShowTransifyKey(shouldShowTransifyKey: boolean) {
    if (env !== 'live' && env !== 'liveish') {
      setShouldShowTransifyKey(shouldShowTransifyKey);
    }
  }

  async function changeLanguage(newLanguage: Language) {
    if (!translations[newLanguage]) {
      translations[newLanguage] = {};
      collectionsByLanguage.current[newLanguage] = {};
      await fetchTranslation(initialCollections, newLanguage);
    }
    setCurrentLanguage(newLanguage);
  }

  const t = React.useCallback(
    (key: string, opts?: StringPlaceholders) => {
      if (shouldShowTransifyKey) {
        return key;
      }
      const mapping = translations[currentLanguage];
      if (
        opts &&
        typeof opts === 'object' &&
        opts.hasOwnProperty('context') &&
        typeof opts.context === 'string' &&
        opts.context
      ) {
        const contextKey = `${key}__${opts.context}`;
        if (Object.prototype.hasOwnProperty.call(mapping, contextKey)) {
          return replacePlaceholders(mapping[contextKey], opts);
        }
      }
      if (Object.prototype.hasOwnProperty.call(mapping, key)) {
        return replacePlaceholders(mapping[key], opts);
      }
      return key;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations, currentLanguage, shouldShowTransifyKey]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tNode = React.useMemo(() => tNodeFactory(t, shouldShowTransifyKey), [
    translations,
    currentLanguage,
    shouldShowTransifyKey,
  ]);

  return {
    checkMissingCollection,
    fetchMissingTranslation,
    updateShouldShowTransifyKey,
    changeLanguage,
    currentLanguage,
    t,
    tNode,
  };
}
